import { useEffect, useRef } from 'react';
import { subscribeToConversation, AiThinkingStepEvent } from '../services/conversationHub';

type SubscribeOptions = Parameters<typeof subscribeToConversation>[0];
type MessageCreatedHandler = SubscribeOptions['onMessageCreated'];
type ConversationUpdatedHandler = SubscribeOptions['onConversationUpdated'];
export type { AiThinkingStepEvent };

interface UseConversationRealtimeSubscriptionOptions {
  enabled: boolean;
  tenantId?: string;
  conversationId?: string;
  onMessageCreated: MessageCreatedHandler;
  onConversationUpdated: ConversationUpdatedHandler;
  onAiThinkingStep?: (evt: AiThinkingStepEvent) => void;
  onReconnected?: () => void | Promise<void>;
  onError?: (message: string) => void;
}

export const useConversationRealtimeSubscription = ({
  enabled,
  tenantId,
  conversationId,
  onMessageCreated,
  onConversationUpdated,
  onAiThinkingStep,
  onReconnected,
  onError,
}: UseConversationRealtimeSubscriptionOptions): void => {
  const handlersRef = useRef({
    onMessageCreated,
    onConversationUpdated,
    onAiThinkingStep,
    onReconnected,
    onError,
  });

  useEffect(() => {
    handlersRef.current = {
      onMessageCreated,
      onConversationUpdated,
      onAiThinkingStep,
      onReconnected,
      onError,
    };
  }, [onConversationUpdated, onError, onMessageCreated, onAiThinkingStep, onReconnected]);

  useEffect(() => {
    if (!enabled || !tenantId || !conversationId) {
      return undefined;
    }

    let disposed = false;
    let unsubscribe: (() => Promise<void>) | null = null;

    const attach = async () => {
      try {
        unsubscribe = await subscribeToConversation({
          tenantId,
          conversationId,
          onMessageCreated: (event) => handlersRef.current.onMessageCreated(event),
          onConversationUpdated: (event) => handlersRef.current.onConversationUpdated(event),
          onAiThinkingStep: (event) => handlersRef.current.onAiThinkingStep?.(event),
          onReconnected: handlersRef.current.onReconnected
            ? () => handlersRef.current.onReconnected?.()
            : undefined,
        });
      } catch (error) {
        if (disposed) return;
        const message = error instanceof Error ? error.message : 'Realtime sync unavailable';
        handlersRef.current.onError?.(message);
      }
    };

    void attach();

    return () => {
      disposed = true;
      if (unsubscribe) {
        void unsubscribe();
      }
    };
  }, [enabled, tenantId, conversationId]);
};

export default useConversationRealtimeSubscription;
